import { Injectable, Inject, InjectionToken } from '@angular/core';
import { NetworkService, API_BASE_URL, AssetTypeMapperService } from '@angular-workspace/core';

/**
 * Injection token for catalogue API path
 */
export const CAT_API_PATH = new InjectionToken<string>('CAT_API_PATH');

/**
 * Interface for search criteria in count API
 */
export interface SearchCriteria {
  searchType: string;
  field: string;
  values: string[];
}

/**
 * Interface for count API request payload
 */
export interface CountRequestPayload {
  searchCriteria: SearchCriteria[];
}

/**
 * Interface for list API request payload
 */
export interface ListRequestPayload {
  searchCriteria: SearchCriteria[];
  filter: string[];
}

/**
 * Catalogue API Service for handling catalogue/asset-related API calls
 * This service uses NetworkService to make HTTP requests
 */
@Injectable({
  providedIn: 'root',
})
export class CatalogueApiService {
  constructor(
    private networkService: NetworkService,
    private assetTypeMapper: AssetTypeMapperService,
    @Inject(API_BASE_URL) private basePathUrl: string,
    @Inject(CAT_API_PATH) private catApiPath: string,
  ) {}

  /**
   * Get all assets from the catalogue API
   * @param page Page number (default: 1)
   * @param size Page size (default: 10)
   * @param search Search term (optional) - if provided, uses POST with search body
   * @param assetTypes Array of asset types to filter by (optional) - if provided, uses POST with filter body
   * @param statuses Array of statuses to filter by (optional)
   * @param dateRange Date range object with start and end dates (optional)
   * @param sort Sort order (default: "itemCreatedAt:desc") - e.g. "label:asc", "accessPolicy:asc", "itemStatus:asc", "itemCreatedAt:asc"
   * @returns Promise with assets data
   */
  async getAllAssets(
    page: number = 1,
    size: number = 10,
    search?: string,
    assetTypes?: string[],
    statuses?: string[],
    dateRange?: { start: Date | null; end: Date | null } | null,
    sort: string = 'itemCreatedAt:desc'
  ): Promise<any> {
    try {
      // Construct the full URL using injected basePathUrl and catApiPath
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('size', size.toString());
      params.append('sort', sort);

      // Check if we need to use POST (search, assetTypes, statuses, or dateRange provided)
      const hasSearch = search && search.trim() !== '';
      const hasAssetTypes = assetTypes && assetTypes.length > 0;
      const hasStatuses = statuses && statuses.length > 0;
      const hasDateRange = dateRange && dateRange.start && dateRange.end;

      if (hasSearch || hasAssetTypes || hasStatuses || hasDateRange) {
        // Use POST request
        const url = `${this.basePathUrl}${this.catApiPath}getAllAssets?${params.toString()}`;

        let payload: any;

        // If only search is provided (no other filters), use the simple search payload format
        if (hasSearch && !hasAssetTypes && !hasStatuses && !hasDateRange) {
          payload = {
            q: search!.trim(),
            autoComplete: true,
            fuzzy: true
          };
        } else {
          // Build POST request body with searchCriteria array for combined filters
          const searchCriteria: any[] = [];

          // Add asset type filter if provided
          if (hasAssetTypes) {
            // Map display types to API format
            const apiAssetTypes = assetTypes.map(type => this.assetTypeMapper.mapAssetTypeToApiFormat(type));
            
            searchCriteria.push({
              searchType: 'term',
              field: 'type',
              values: apiAssetTypes
            });
          }

          // Add status filter if provided
          if (hasStatuses) {
            // Map display statuses to API format (uppercase)
            const apiStatuses = statuses.map(status => status.toUpperCase());
            
            searchCriteria.push({
              searchType: 'term',
              field: 'publishStatus',
              values: apiStatuses
            });
          }

          // Add date range filter if provided
          if (hasDateRange && dateRange.start && dateRange.end) {
            const startDate = new Date(dateRange.start);
            const endDate = new Date(dateRange.end);
            
            // Format dates as ISO strings (start at 00:00:00, end at 23:59:59)
            const startDateStr = startDate.toISOString().split('T')[0] + 'T00:00:00';
            const endDateStr = endDate.toISOString().split('T')[0] + 'T23:59:59';
            
            searchCriteria.push({
              searchType: 'betweenTemporal',
              field: 'itemCreatedAt',
              values: [startDateStr, endDateStr]
            });
          }

          // If search is provided, include q, autoComplete, fuzzy at top level along with searchCriteria
          if (hasSearch) {
            payload = {
              q: search!.trim(),
              autoComplete: true,
              fuzzy: true,
              searchCriteria: searchCriteria
            };
          } else {
            // No search, just wrap searchCriteria in the expected payload format
            payload = {
              searchCriteria: searchCriteria
            };
          }
        }

        // Prepare headers (token is added automatically by interceptor)
        const headers = {
          'Content-Type': 'application/json',
        };

        // Make POST API call
        const assetsData = await this.networkService.post_api(
          url,
          payload,
          headers,
          true, // isToasterRequired - set to true to show error toasters
          true  // isLoaderRequired - set to true to show loader
        );

        return assetsData;
      } else {
        // No search or assetTypes - use GET request
        // Add status parameters if provided
        if (statuses && statuses.length > 0) {
          statuses.forEach(status => {
            params.append('status', status.toUpperCase());
          });
        }

        // Add date range parameters if provided
        if (dateRange && dateRange.start && dateRange.end) {
          const startDate = new Date(dateRange.start);
          const endDate = new Date(dateRange.end);
          
          // Format dates as ISO strings (YYYY-MM-DDTHH:mm:ss)
          const startDateStr = startDate.toISOString().split('T')[0] + 'T00:00:00';
          const endDateStr = endDate.toISOString().split('T')[0] + 'T23:59:59';
          
          params.append('startDate', startDateStr);
          params.append('endDate', endDateStr);
        }

        const url = `${this.basePathUrl}${this.catApiPath}getAllAssets?${params.toString()}`;

        // Make GET API call using NetworkService (headers are added automatically by interceptor)
        const assetsData = await this.networkService.get_api(
          url,
          undefined,
          false, // disableToaster - set to true if you don't want error toasters
        );

        return assetsData;
      }
    } catch (error) {
      console.error('Error fetching all assets:', error);
      throw error;
    }
  }

  /**
   * Get all assets from the catalogue API using GET method
   * Simple GET request without filters
   * @param page Page number (default: 1)
   * @param size Page size (default: 10)
   * @param sort Sort order (default: "itemCreatedAt:desc")
   * @param entityType Optional entity type filter (e.g., "DATABANK", "AIMODEL", "APPS")
   * @returns Promise with assets data
   */
  async getAllAssetsGet(
    page: number = 1,
    size: number = 10,
    sort: string = 'itemCreatedAt:desc',
    entityType?: string
  ): Promise<any> {
    try {
      // Construct the full URL using injected basePathUrl and catApiPath
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('size', size.toString());
      params.append('sort', sort);

      // Add entityType parameter if provided
      if (entityType) {
        params.append('entityType', entityType);
      }

      const url = `${this.basePathUrl}${this.catApiPath}getAllAssets?${params.toString()}`;

      // Make GET API call using NetworkService (headers are added automatically by interceptor)
      const assetsData = await this.networkService.get_api(
        url,
        undefined,
        false, // disableToaster - set to true if you don't want error toasters
      );

      return assetsData;
    } catch (error) {
      console.error('Error fetching all assets:', error);
      throw error;
    }
  }

  /**
   * Get asset/item details by ID from the catalogue API
   * @param assetId The asset/item ID to fetch
   * @returns Promise with asset/item data
   */
  async getItemById(assetId: string): Promise<any> {
    try {
      // Construct the full URL using injected basePathUrl and catApiPath
      const params = new URLSearchParams();
      params.append('id', assetId);

      const url = `${this.basePathUrl}${this.catApiPath}item?${params.toString()}`;

      // Make the API call using NetworkService (headers are added automatically by interceptor)
      const itemData = await this.networkService.get_api(
        url,
        undefined,
        false, // disableToaster - set to true if you don't want error toasters
      );

      return itemData;
    } catch (error) {
      console.error(`Error fetching item by ID ${assetId}:`, error);
      throw error;
    }
  }

  /**
   * Get count of assets from the catalogue API for stats in landing page
   * @param searchCriteria Array of search criteria objects to filter by
   * @returns Promise with count data
   */
  async getCount(searchCriteria: SearchCriteria[]): Promise<any> {
    const url = `${this.basePathUrl}${this.catApiPath}count`;

    const payload: CountRequestPayload = {
      searchCriteria: searchCriteria
    };

    const countData = await this.networkService.post_api(
      url,
      payload,
      undefined, // headers - not needed, interceptors handle Content-Type and Authorization
      true, // isToasterRequired - set to true to show error toasters
      false // isLoaderRequired - set to false to avoid loader for count requests
    );

    return countData;
  }

  /**
   * Search all assets using the search endpoint
   * @param searchQuery The search query string from the component (optional - empty string fetches all)
   * @param assetTypes Array of asset types to filter by (e.g., ["Dataset", "Model", "App"])
   * @param customFilter Array of fields to return (e.g., ["id", "name", "type", "label"]) (optional)
   * @param page Page number (default: 1)
   * @param size Page size (default: 10)
   * @param sort Sort order (default: "name:asc;itemCreatedAt:asc;")
   * @param additionalSearchCriteria Additional search criteria to merge with type filter (optional)
   * @returns Promise with search results
   */
  async searchAllAssets(
    searchQuery: string = '',
    assetTypes: string[] = [],
    customFilter: string[] = [],
    page: number = 1,
    size: number = 10,
    sort: string = 'name:asc;itemCreatedAt:asc;',
    additionalSearchCriteria: SearchCriteria[] = []
  ): Promise<any> {
    try {
      // Build URL with pagination parameters
      const params = new URLSearchParams();
      params.append('sort', sort);
      params.append('page', page.toString());
      params.append('size', size.toString());

      const url = `${this.basePathUrl}${this.catApiPath}search?${params.toString()}`;

      // Map display types to API format
      const apiAssetTypes = assetTypes.map(type => this.assetTypeMapper.mapAssetTypeToApiFormat(type));

      // Build POST request payload
      const payload: any = {};

      // Only add search query if provided
      if (searchQuery && searchQuery.trim() !== '') {
        payload.q = searchQuery.trim();
        payload.fuzzy = true;
        payload.autoComplete = true;
      }

      // Build search criteria array
      const searchCriteria: SearchCriteria[] = [];

      // Add search criteria for asset types if provided
      if (apiAssetTypes.length > 0) {
        searchCriteria.push({
            searchType: 'term',
            field: 'type',
            values: apiAssetTypes
        });
      }

      // Merge additional search criteria
      if (additionalSearchCriteria.length > 0) {
        searchCriteria.push(...additionalSearchCriteria);
      }

      // Add searchCriteria to payload if it has items
      if (searchCriteria.length > 0) {
        payload.searchCriteria = searchCriteria;
      }

      // Add custom filter if provided
      if (customFilter.length > 0) {
        payload.filter = customFilter;
      }

      // Make POST API call
      const searchResults = await this.networkService.post_api(
        url,
        payload,
        undefined,
        true, // isToasterRequired - set to true to show error toasters
        false // isLoaderRequired - set to false for search requests
      );

      return searchResults;
    } catch (error) {
      console.error('Error searching assets:', error);
      throw error;
    }
  }

  /**
   * Get list of assets from the catalogue API using the list endpoint
   * @param searchCriteria Array of search criteria objects to filter by
   * @param filter Array of fields to return (e.g., ["concepts", "tags", "organization", "fileFormat", "organizationType", "accessPolicy", "resourceServer.url"])
   * @returns Promise with list data
   */
  async getFilterList(
    searchCriteria: SearchCriteria[],
    filter: string[]
  ): Promise<any> {
    try {
      const url = `${this.basePathUrl}${this.catApiPath}list`;

      const payload: ListRequestPayload = {
        searchCriteria: searchCriteria,
        filter: filter
      };

      const listData = await this.networkService.post_api(
        url,
        payload,
        undefined, // headers - not needed, interceptors handle Content-Type and Authorization
        true, // isToasterRequired - set to true to show error toasters
        true // isLoaderRequired - set to true to show loader
      );

      return listData;
    } catch (error) {
      console.error('Error fetching list:', error);
      throw error;
    }
  }

  /**
   * Create a new asset item in the catalogue API
   * @param payload The payload object containing asset item data (will be sent from component)
   * @returns Promise with created asset item data
   */
  async createAssetItem(payload: any): Promise<any> {
    try {
      const url = `${this.basePathUrl}${this.catApiPath}item`;

      const assetItemData = await this.networkService.post_api(
        url,
        payload,
        undefined, // headers - not needed, interceptors handle Content-Type and Authorization
        true, // isToasterRequired - set to true to show error toasters
        true // isLoaderRequired - set to true to show loader
      );

      return assetItemData;
    } catch (error) {
      console.error('Error creating asset item:', error);
      throw error;
    }
  }

  /**
   * Create a new usecase in the catalogue API
   * @param payload The payload object containing usecase data (will be sent from component)
   * @returns Promise with created usecase data
   */
  async createUseCase(payload: any): Promise<any> {
    try {
      const url = `${this.basePathUrl}${this.catApiPath}item`;

      const usecaseData = await this.networkService.post_api(
        url,
        payload,
        undefined, // headers - not needed, interceptors handle Content-Type and Authorization
        true, // isToasterRequired - set to true to show error toasters
        true // isLoaderRequired - set to true to show loader
      );

      return usecaseData;
    } catch (error) {
      console.error('Error creating usecase:', error);
      throw error;
    }
  }

  /**
   * Create a new item in the catalogue API
   * @param payload The payload object containing item data (will be sent from component)
   * @returns Promise with created item data
   */
  async createItem(payload: any): Promise<any> {
    try {
      const url = `${this.basePathUrl}${this.catApiPath}item`;

      const itemData = await this.networkService.post_api(
        url,
        payload,
        undefined, // headers - not needed, interceptors handle Content-Type and Authorization
        true, // isToasterRequired - set to true to show error toasters
        true // isLoaderRequired - set to true to show loader
      );

      return itemData;
    } catch (error) {
      console.error('Error creating item:', error);
      throw error;
    }
  }

  /**
   * Update an existing item in the catalogue API.
   * ENDPOINT: PUT {basePathUrl}{catApiPath}item (e.g. https://v2.dev.controlplane.iudx.io/iudx/v2/cat/item)
   * @param payload The payload object containing item data (id, type, name, description, shortDescription, tags, license, industry, organizationId, organizationType, uploadedBy, organization, department, geoCoverage, yearRange, resourceServer, uploadFrequency, dataBankType, fileFormat, dataDescriptor, accessPolicy, etc.)
   * @returns Promise with updated item data
   */
  async updateItem(payload: any): Promise<any> {
    try {
      const url = `${this.basePathUrl}${this.catApiPath}item`;

      const itemData = await this.networkService.put_api(
        url,
        payload,
        undefined // headers - not needed, interceptors handle Content-Type and Authorization
      );

      return itemData;
    } catch (error) {
      console.error('Error updating item:', error);
      throw error;
    }
  }

  /**
   * Hide or unhide an item in the catalogue API
   * @param id The ID of the item to hide/unhide
   * @param payload The payload object containing data to update (will be sent from component)
   * @returns Promise with the response data
   */
  async hideOrUnhideItems(id: string, payload: any): Promise<any> {
    try {
      // Construct the full URL using injected basePathUrl and catApiPath
      const params = new URLSearchParams();
      params.append('id', id);

      const url = `${this.basePathUrl}${this.catApiPath}organisation/asset?${params.toString()}`;

      // Make PATCH API call using NetworkService (headers are added automatically by interceptor)
      const response = await this.networkService.patch_api(
        url,
        payload,
        undefined // headers - not needed, interceptors handle Content-Type and Authorization
      );

      return response;
    } catch (error) {
      console.error(`Error hiding/unhiding item ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete an item from the catalogue API
   * @param id The ID of the item to delete
   * @returns Promise with the response data
   */
  async deleteItem(id: string): Promise<any> {
    try {
      // Construct the full URL using injected basePathUrl and catApiPath
      const params = new URLSearchParams();
      params.append('id', id);

      const url = `${this.basePathUrl}${this.catApiPath}item?${params.toString()}`;

      // Make DELETE API call using NetworkService (headers are added automatically by interceptor)
      const response = await this.networkService.delete_api(
        url,
        undefined, // body - not needed for DELETE
        undefined // headers - not needed, interceptors handle Content-Type and Authorization
      );

      return response;
    } catch (error) {
      console.error(`Error deleting item ${id}:`, error);
      throw error;
    }
  }

  /**
   * Get my assets from the catalogue API
   * @param filterPayload Optional filter payload. Can be:
   *   - Array format: [{ "searchType": "term", "field": "type", "values": ["adex:DataBank"] }]
   *   - Object format with search: { "q": "asr", "autoComplete": true, "fuzzy": true, "searchCriteria": [...] }
   * @param page When provided with `size`, appended as query params (same as GET `getAssets`) so POST filtered search is paginated.
   * @param size Page size (use with `page`).
   * @param sort Sort string, e.g. `itemCreatedAt:desc` (optional; omitted from URL if not set).
   * @returns Promise with my assets data
   */
  async getMyAssets(filterPayload?: any[] | any, page?: number, size?: number, sort?: string): Promise<any> {
    try {
      const baseUrl = `${this.basePathUrl}${this.catApiPath}search/myassets`;
      const withPaginationQuery = (path: string): string => {
        if (page == null || size == null) return path;
        const params = new URLSearchParams();
        params.append('page', String(page));
        params.append('size', String(size));
        if (sort != null && String(sort).trim() !== '') {
          params.append('sort', String(sort));
        }
        return `${path}?${params.toString()}`;
      };

      // If filter payload is provided, use POST request with payload
      if (filterPayload) {
        // Check if payload is an array with items or an object with search query or searchCriteria
        const hasPayload = Array.isArray(filterPayload) 
          ? filterPayload.length > 0 
          : (filterPayload.q || (filterPayload.searchCriteria && filterPayload.searchCriteria.length > 0));
        
        if (hasPayload) {
          const headers = {
            'Content-Type': 'application/json',
          };

          const url = withPaginationQuery(baseUrl);

          // Make POST API call using NetworkService (headers are added automatically by interceptor)
          const myAssetsData = await this.networkService.post_api(
            url,
            filterPayload,
            headers,
            false, // disableToaster - set to true if you don't want error toasters
          );

          return myAssetsData;
        }
      }

      // Make GET API call using NetworkService (headers are added automatically by interceptor)
      const myAssetsData = await this.networkService.get_api(
        withPaginationQuery(baseUrl),
        undefined,
        false, // disableToaster - set to true if you don't want error toasters
      );

      return myAssetsData;
    } catch (error) {
      console.error('Error fetching my assets:', error);
      throw error;
    }
  }

  /**
   * Get assets from the catalogue API using GET method
   * @param page Page number (default: 1)
   * @param size Page size (default: 10)
   * @param sort Sort order (default: "itemCreatedAt:desc")
   * @returns Promise with assets data
   */
  async getAssets(
    page: number = 1,
    size: number = 10,
    sort: string = 'itemCreatedAt:desc'
  ): Promise<any> {
    try {
      // Construct the full URL using injected basePathUrl and catApiPath
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('size', size.toString());
      params.append('sort', sort);

      const url = `${this.basePathUrl}${this.catApiPath}search/myassets?${params.toString()}`;

      // Make GET API call using NetworkService (headers are added automatically by interceptor)
      const assetsData = await this.networkService.get_api(
        url,
        undefined,
        false, // disableToaster - set to true if you don't want error toasters
      );

      return assetsData;
    } catch (error) {
      console.error('Error fetching assets:', error);
      throw error;
    }
  }

  /**
   * Get organisation assets from the catalogue API
   * @param page Page number (default: 1)
   * @param size Page size (default: 10)
   * @param sort Sort order (default: "itemCreatedAt:desc")
   * @param searchCriteria Optional array of search criteria filters. If not provided, uses default asset types filter
   * @returns Promise with organisation assets data
   */
  async getOrganisationAsset(
    page: number = 1,
    size: number = 10,
    sort: string = 'itemCreatedAt:desc',
    searchCriteria?: SearchCriteria[]
  ): Promise<any> {
    try {
      // Construct the full URL using injected basePathUrl and catApiPath
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('size', size.toString());
      params.append('sort', sort);

      const url = `${this.basePathUrl}${this.catApiPath}organisation/asset?${params.toString()}`;

      // Build payload with searchCriteria
      // If searchCriteria is provided, use it; otherwise use default asset types filter
      let finalSearchCriteria: SearchCriteria[];
      
      if (searchCriteria && searchCriteria.length > 0) {
        finalSearchCriteria = searchCriteria;
      } else {
        // Default payload with searchCriteria for asset types
        finalSearchCriteria = [
          {
            searchType: 'term',
            field: 'type',
            values: [
              'adex:DataBank',
              'adex:aiModel',
              'adex:Apps'
            ]
          }
        ];
      }

      const payload = {
        searchCriteria: finalSearchCriteria
      };

      // Console log for Manage-contribution asset API
      console.log('=== getOrganisationAsset API Call ===');
      console.log('URL:', url);
      console.log('Payload:', payload);
      console.log('Payload (JSON):', JSON.stringify(payload, null, 2));

      // Prepare headers (token is added automatically by interceptor)
      const headers = {
        'Content-Type': 'application/json',
      };

      // Make POST API call using NetworkService (headers are added automatically by interceptor)
      const organisationAssetsData = await this.networkService.post_api(
        url,
        payload,
        headers,
        true, // isToasterRequired - set to true to show error toasters
        true  // isLoaderRequired - set to true to show loader
      );

      console.log('Response:', organisationAssetsData);
      console.log('Response (JSON):', JSON.stringify(organisationAssetsData, null, 2));
      console.log('=== End getOrganisationAsset API Call ===');

      return organisationAssetsData;
    } catch (error) {
      console.error('Error fetching organisation assets:', error);
      throw error;
    }
  }

  /**
   * Get organisation publish assets from the catalogue API
   * @param page Page number (default: 1)
   * @param size Page size (default: 10)
   * @param sort Sort order (default: "itemCreatedAt:desc")
   * @param publishStatuses Optional array of statuses to filter by (e.g. PENDING, ACTIVE, REJECTED). Defaults to ['PENDING'].
   * @returns Promise with organisation publish assets data
   */
  async OrgPublishAssets(
    page: number = 1,
    size: number = 10,
    sort: string = 'itemCreatedAt:desc',
    publishStatuses: string[] = ['PENDING']
  ): Promise<any> {
    try {
      const statuses = Array.isArray(publishStatuses) && publishStatuses.length > 0
        ? publishStatuses
        : ['PENDING'];

      // Construct the full URL using injected basePathUrl and catApiPath
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('size', size.toString());
      for (const s of statuses) {
        params.append('publishStatus', s);
      }
      params.append('sort', sort);

      const url = `${this.basePathUrl}${this.catApiPath}organisation/asset?${params.toString()}`;

      // Console log for OrgPublishAssets API Call
      console.log('=== OrgPublishAssets API Call ===');
      console.log('URL:', url);

      // Make GET API call using NetworkService (headers are added automatically by interceptor)
      const publishAssetsData = await this.networkService.get_api(
        url,
        undefined,
        false, // disableToaster - set to true if you don't want error toasters
      );

      console.log('Response:', publishAssetsData);
      console.log('Response (JSON):', JSON.stringify(publishAssetsData, null, 2));
      console.log('=== End OrgPublishAssets API Call ===');

      return publishAssetsData;
    } catch (error) {
      console.error('Error fetching organisation publish assets:', error);
      throw error;
    }
  }

  /**
   * Publish/approve an asset in the catalogue API
   * Updates the publish status and data upload status of an asset
   * @param assetId The ID of the asset to publish
   * @param payload The payload object containing publishStatus and dataUploadStatus
   * @returns Promise with the response data
   */
  async postPublishAssets(assetId: string, payload: any): Promise<any> {
    try {
      // Construct the full URL using injected basePathUrl and catApiPath
      const params = new URLSearchParams();
      params.append('id', assetId);

      const url = `${this.basePathUrl}${this.catApiPath}organisation/asset?${params.toString()}`;

      // Console log for postPublishAssets API Call
      console.log('=== postPublishAssets API Call (PATCH) ===');
      console.log('URL:', url);
      console.log('Payload:', payload);
      console.log('Payload (JSON):', JSON.stringify(payload, null, 2));

      // Make PATCH API call using NetworkService (headers are added automatically by interceptor)
      const response = await this.networkService.patch_api(
        url,
        payload,
        undefined // headers - not needed, interceptors handle Content-Type and Authorization
      );

      console.log('Response:', response);
      console.log('Response (JSON):', JSON.stringify(response, null, 2));
      console.log('=== End postPublishAssets API Call ===');

      return response;
    } catch (error) {
      console.error(`Error publishing asset ${assetId}:`, error);
      throw error;
    }
  }

  /**
   * Search assets by IDs from interaction API response
   * Used to fetch full asset details for bookmarked/liked items
   * @param assetIds Array of asset IDs to search for
   * @param assetType Optional asset type to filter by (e.g., "Dataset", "Model", "Usecase"). If provided, adds type filter to searchCriteria
   * @param page Page number (default: 1)
   * @param size Page size (default: 10)
   * @param sort Sort order (default: "itemCreatedAt:desc")
   * @returns Promise with search results containing full asset details
   */
  async searchAssetsByIds(
    assetIds: string[],
    assetType?: string,
    page: number = 1,
    size: number = 10,
    sort: string = 'itemCreatedAt:desc'
  ): Promise<any> {
    try {
      // Build URL with pagination parameters
      const params = new URLSearchParams();
      params.append('sort', sort);
      params.append('page', page.toString());
      params.append('size', size.toString());

      const url = `${this.basePathUrl}${this.catApiPath}search?${params.toString()}`;

      // Build POST request payload with searchCriteria
      const searchCriteria: any[] = [];

      // Add type filter if assetType is provided
      if (assetType) {
        // Map display type to API format (e.g., "Dataset" -> "adex:DataBank")
        const apiAssetType = this.assetTypeMapper.mapAssetTypeToApiFormat(assetType);
        searchCriteria.push({
          searchType: 'term',
          field: 'type',
          values: [apiAssetType]
        });
      }

      // Add ID filter
      searchCriteria.push({
        searchType: 'term',
        field: 'id',
        values: assetIds
      });

      const payload = {
        searchCriteria: searchCriteria
      };

      // Make POST API call
      const searchResults = await this.networkService.post_api(
        url,
        payload,
        undefined,
        true, // isToasterRequired - set to true to show error toasters
        false // isLoaderRequired - set to false for search requests
      );

      return searchResults;
    } catch (error) {
      console.error('Error searching assets by IDs:', error);
      throw error;
    }
  }

  /**
   * Get temporal entities from the resource server (NGSI-LD temporal/entities).
   * Uses retry logic for transient failures. Supports cancellation via AbortSignal.
   * @param entityId Dataset/entity ID
   * @param timeFrom Start time (ISO string, e.g. 2026-01-02T00:00:00Z)
   * @param timeTo End time (ISO string, e.g. 2026-02-01T23:59:59Z)
   * @param temporalBaseUrl Base URL for the resource server (e.g. environment.baseRsPathUrl)
   * @param options Optional: maxRetries (default 3), retryDelayMs (default 1000), signal (AbortSignal to cancel)
   * @returns Promise with temporal entities response; rejects with AbortError if signal is aborted
   */
  async getTemporalEntities(
    entityId: string,
    timeFrom: string,
    timeTo: string,
    temporalBaseUrl: string,
    options?: { maxRetries?: number; retryDelayMs?: number; signal?: AbortSignal }
  ): Promise<any> {
    const maxRetries = options?.maxRetries ?? 3;
    const retryDelayMs = options?.retryDelayMs ?? 1000;
    const signal = options?.signal;
    const base = temporalBaseUrl.endsWith('/') ? temporalBaseUrl : temporalBaseUrl + '/';
    const params = new URLSearchParams({
      id: entityId,
      q: `id==${entityId}`,
      timerel: 'during',
      time: timeFrom,
      endtime: timeTo,
    });
    const url = `${base}rsp/ngsi-ld/v1/temporal/entities?${params.toString()}`;

    let lastError: any;
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const data = await this.networkService.get_api(
          url,
          undefined,
          true, // disableToaster - caller handles toaster
          signal
        );
        return data;
      } catch (err: any) {
        lastError = err;
        // Don't retry if user cancelled
        if (err?.name === 'AbortError') {
          throw err;
        }
        if (attempt < maxRetries) {
          await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
        }
      }
    }
    throw lastError;
  }

  /**
   * Search entities (NGSI-LD v2 entities search).
   * POST to {baseUrl}/ngsi-ld/v2/entities/{entityId}/search with body from component.
   * @param entityId Dataset/entity ID
   * @param payload Request body (e.g. { searchCriteria: [{ searchType, field, values }] })
   * @param baseUrl Base URL for the dataplane/resource server (e.g. environment.baseRsPathUrl)
   * @param options Optional: page (default 1), size (default 10)
   * @returns Promise with search response
   */
  async searchEntities(
    entityId: string,
    payload: Record<string, unknown>,
    baseUrl: string,
    options?: { page?: number; size?: number }
  ): Promise<any> {
    const page = options?.page ?? 1;
    const size = options?.size ?? 10;
    const base = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const url = `${base}/ngsi-ld/v2/entities/${entityId}/search?page=${page}&size=${size}`;

    const headers = {
      'Content-Type': 'application/json',
    };

    const data = await this.networkService.post_api(
      url,
      payload,
      headers,
      true, // disableToaster - caller handles toaster
      false // isLoaderRequired - caller shows own loader
    );

    return data;
  }

}

